import React, { useMemo, useState, useRef } from 'react';
import type { DayInfo } from '../../pages/psychologist/tabs/sessions/types';
import { useWidth } from '../../hooks/useWidth';

/**
 * WeekNavigator renders a horizontal row of day-selector buttons flanked by
 * prev/next-week arrow buttons. It is shared by the Sessions tab (and any future
 * weekly view that operates on the same DayInfo shape).
 *
 * Keyboard navigation: ArrowLeft/Right move between days, Home/End jump to
 * first/last day of the displayed week.
 */

type WeekNavigatorProps = {
  days: DayInfo[];
  onPrevWeek: () => void;
  onNextWeek: () => void;
  selectedISO: string;
  onSelect: (dateISO: string) => void;
  disabled?: boolean;
};

export const WeekNavigator: React.FC<WeekNavigatorProps> = ({
  days,
  onPrevWeek,
  onNextWeek,
  selectedISO,
  onSelect,
  disabled = false,
}) => {
  const { size } = useWidth();
  const isMobile = size === 's';

  // Mobile: two windows — Mon/Tue/Wed (indices 0-2) and Thu/Fri/Sat/Sun (indices 3-6).
  // weeks are now Monday-based (Mon→Sun) so the split is always at index 3.
  const monWedDays = days.slice(0, 3);
  const thuSunDays = days.slice(3);

  // Initialize to the window containing today
  const [mobileWindowStart, setMobileWindowStart] = useState<0 | 3>(() => {
    const dow = new Date().getDay(); // 0=Sun … 6=Sat
    return (dow === 0 || dow >= 4) ? 3 : 0;
  });

  const visibleDays = isMobile ? (mobileWindowStart === 0 ? monWedDays : thuSunDays) : days;

  // Mobile window navigation
  const swipeTouchRef = useRef<{ x: number; y: number } | null>(null);

  const onMobilePrev = () => {
    if (mobileWindowStart === 3) { setMobileWindowStart(0); return; }
    onPrevWeek();
    setMobileWindowStart(3);
  };

  const onMobileNext = () => {
    if (mobileWindowStart === 0) { setMobileWindowStart(3); return; }
    onNextWeek();
    setMobileWindowStart(0);
  };

  const selectedIndex = useMemo(
    () => days.findIndex((d) => d.dateISO === selectedISO),
    [days, selectedISO],
  );

  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const idx = Math.max(0, selectedIndex - 1);
      onSelect(days[idx]?.dateISO || selectedISO);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const idx = Math.min(days.length - 1, selectedIndex + 1);
      onSelect(days[idx]?.dateISO || selectedISO);
    }
    if (e.key === 'Home') {
      e.preventDefault();
      onSelect(days[0]?.dateISO || selectedISO);
    }
    if (e.key === 'End') {
      e.preventDefault();
      onSelect(days[days.length - 1]?.dateISO || selectedISO);
    }
  };

  return (
    <div className="sessions-week">
      <style>{`
        .sessions-week { display: flex; align-items: center; justify-content: center; gap: 3.5rem; }
        .w-arrow { background: white; border: 1px solid #e5e7eb; width: 32px; height: 32px; border-radius: 9999px; display:flex; align-items:center; justify-content:center; cursor: pointer; flex-shrink: 0; }
        .w-days { display: grid; grid-auto-flow: column; grid-auto-columns: 84px; gap: 0.5rem; overflow-x: auto; padding-bottom: 2px; outline: none; justify-content: center; }
        .w-day { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #ffffff; text-align: center; cursor: pointer; box-shadow: 0 1px 2px rgba(17,24,39,0.04); transition: box-shadow .15s ease, border-color .15s ease; }
        .w-day strong { display:block; font-size: 1.25rem; color: #111827; }
        .w-day small { display:block; font-size: 0.72rem; color: #6b7280; font-weight:600; }
        .w-day .count { display:block; margin-top: 4px; font-size: 0.72rem; color: #6D57BC; font-weight: 700; }
        .w-day:hover { border-color:#d1d5db; box-shadow: 0 3px 8px rgba(17,24,39,0.06); }
        .w-day.sel { background: #6D57BC; border-color: transparent; box-shadow: 0 4px 12px rgba(109,87,188,0.3); }
        .w-day.sel strong, .w-day.sel small, .w-day.sel .count { color: #ffffff; }
        .pill { display:inline-block; padding:2px 6px; border-radius:9999px; background:#F1EEFF; color:#6D57BC; font-weight:700; font-size:0.68rem; }
        .w-day.sel .pill { background: rgba(255,255,255,0.2); color:#fff; }
        .w-days:focus-visible { outline: 2px solid #6D57BC; outline-offset: 2px; border-radius: 8px; }

        @media (max-width: 1023px) {
          .sessions-week { gap: 0.4rem; width: 100%; box-sizing: border-box; }
          .w-arrow { width: 28px; height: 28px; font-size: 1rem; }
          .w-days {
            grid-auto-columns: minmax(0, 1fr);
            gap: 0.3rem;
            flex: 1;
            min-width: 0;
            overflow: hidden;
          }
          .w-day {
            padding: 8px 4px;
            border-radius: 10px;
            width: 100%;
          }
          .w-day strong { font-size: 1.05rem; }
          .w-day small {
            font-size: 0.6rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: clip;
            max-width: 100%;
          }
          .w-day .count { font-size: 0.58rem; margin-top: 2px; }
          .pill { font-size: 0.55rem; padding: 1px 4px; }
        }
      `}</style>
      <button
        className="w-arrow"
        onClick={isMobile ? onMobilePrev : onPrevWeek}
        aria-label="Semana anterior"
        disabled={disabled}
      >
        ‹
      </button>
      <div
        className="w-days"
        role="tablist"
        aria-label="Selector de días"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={disabled ? undefined : onKey}
        onTouchStart={isMobile ? (e) => {
          const t = e.touches[0];
          swipeTouchRef.current = { x: t.clientX, y: t.clientY };
        } : undefined}
        onTouchEnd={isMobile ? (e) => {
          if (!swipeTouchRef.current) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - swipeTouchRef.current.x;
          const dy = t.clientY - swipeTouchRef.current.y;
          swipeTouchRef.current = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          if (dx < 0) onMobileNext(); else onMobilePrev();
        } : undefined}
      >
        {visibleDays.map((d) => (
          <button
            key={d.dateISO}
            role="tab"
            aria-selected={selectedISO === d.dateISO}
            className={`w-day ${selectedISO === d.dateISO ? 'sel' : ''}`}
            onClick={() => !disabled && onSelect(d.dateISO)}
            disabled={disabled}
            style={disabled ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
          >
            <strong>{d.dayNumber}</strong>
            <small>
              {d.isToday ? <span className="pill">Hoy</span> : (isMobile ? d.weekdayName.slice(0, 3) : d.weekdayName)}
            </small>
            <span className="count">{d.sessionsCount}{isMobile ? 's' : ' ses.'}</span>
          </button>
        ))}
      </div>
      <button
        className="w-arrow"
        onClick={isMobile ? onMobileNext : onNextWeek}
        aria-label="Semana siguiente"
        disabled={disabled}
      >
        ›
      </button>
    </div>
  );
};

export default WeekNavigator;
